import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import '../AdminDashboard/BalanceEnquiry.css';
import './SecondaryBalanceEnquiry.css';

const SecondaryBalanceEnquiry = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [dailyBalances, setDailyBalances] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBalanceSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalanceSummary = async () => {
    setLoading(true);
    setError('');
    setDailyBalances(null);

    try {
      let url = `${API_BASE_URL}/api/secondary-balance-enquiry`;

      if (dateFrom && dateTo) {
        url += `?date_from=${dateFrom}&date_to=${dateTo}`;
      }

      const requests = [fetch(url)];
      if (dateFrom && dateTo && dateFrom === dateTo) {
        requests.push(
          fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${dateFrom}`)
        );
      }

      const [response, dailyResponse] = await Promise.all(requests);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch balance data');
      }

      const data = await response.json();
      setBalanceData(data);

      if (dailyResponse) {
        if (dailyResponse.ok) {
          const dailyData = await dailyResponse.json();
          setDailyBalances(dailyData);
        } else {
          const payload = await dailyResponse.json().catch(() => ({}));
          throw new Error(payload.error || payload.message || 'Failed to fetch daily opening/closing balances');
        }
      }
    } catch (err) {
      console.error('Failed to fetch secondary balance enquiry data:', err);
      setError(err.message || 'Unexpected error occurred while loading balance data.');
      setBalanceData(null);
      setDailyBalances(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      alert('From date cannot be greater than To date.');
      return;
    }
    fetchBalanceSummary();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchBalanceSummary();
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const balanceRows = useMemo(() => {
    if (!balanceData) return [];

    const rows = [
      // Debit Items
      { 
        label: 'General Loan (Debit)', 
        value: balanceData.secondaryGeneralLoanDebit || 0,
        type: 'debit',
        category: 'loans'
      },
      { 
        label: 'CD Loan (Debit)', 
        value: balanceData.secondaryCdLoanDebit || 0,
        type: 'debit',
        category: 'loans'
      },
      { 
        label: 'TBD Loan (Debit)', 
        value: balanceData.secondaryTbdLoanDebit || 0,
        type: 'debit',
        category: 'loans'
      },
      
      // Credit Items - Interest (Profit)
      { 
        label: 'GeneralLoan Interest (Credit)', 
        value: balanceData.secondaryGeneralLoanInterest || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'interest'
      },
      { 
        label: 'CD Loan Interest (Credit)', 
        value: balanceData.secondaryCdLoanInterest || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'interest'
      },
      { 
        label: 'TBD Loan Interest (Credit)', 
        value: balanceData.secondaryTbdLoanInterest || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'interest'
      },
      
      // Credit Items - EMI
      { 
        label: 'GeneralLoan EMI (Credit)', 
        value: balanceData.secondaryGeneralLoanEmi || 0,
        type: 'credit',
        category: 'emi'
      },
      { 
        label: 'CD Loan EMI (Credit)', 
        value: balanceData.secondaryCdLoanEmi || 0,
        type: 'credit',
        category: 'emi'
      },
      { 
        label: 'TBD Loan EMI (Credit)', 
        value: balanceData.secondaryTbdLoanEmi || 0,
        type: 'credit',
        category: 'emi'
      },
      
      // Credit Items - Overdue (Profit)
      { 
        label: 'GL Overdue (Credit)', 
        value: balanceData.secondaryGlOverdue || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'overdue'
      },
      { 
        label: 'CD Overdue (Credit)', 
        value: balanceData.secondaryCdOverdue || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'overdue'
      },
      { 
        label: 'TBD Overdue (Credit)', 
        value: balanceData.secondaryTbdOverdue || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'overdue'
      },
      
      // Other Credit Items
      { 
        label: 'Partner Transactions (Credit)', 
        value: balanceData.secondaryPartnerCredits || 0,
        type: 'credit',
        category: 'other'
      },
      
      // Other Debit Items
      { 
        label: 'Expense (Debit)', 
        value: balanceData.secondaryExpenseDebit || 0,
        type: 'debit',
        category: 'other'
      },
      { 
        label: 'Partner Meeting (Debit)', 
        value: balanceData.secondaryPartnerMeetingDebit || 0,
        type: 'debit',
        category: 'other'
      },
      { 
        label: 'Lucky Draw (Debit)', 
        value: balanceData.secondaryRdDebit || 0,
        type: 'debit',
        category: 'other'
      },
      
      // Profit Items - Document Charges
      { 
        label: 'Document charges (Credit)', 
        value: balanceData.secondaryDocumentChargesCredit || 0,
        type: 'credit',
        category: 'profit',
        profitType: 'document'
      },
      
      // Other Credit Items
      { 
        label: 'Total amount collected from the FD (Credit)', 
        value: balanceData.secondaryRdCredit || 0,
        type: 'credit',
        category: 'other'
      },
      
      // Pigmy Items
      { 
        label: 'Pigmy Credit', 
        value: balanceData.secondaryPigmyCredit || 0,
        type: 'credit',
        category: 'other'
      },
      { 
        label: 'Pigmy Debit', 
        value: balanceData.secondaryPigmyDebit || 0,
        type: 'debit',
        category: 'other'
      },
    ];

    // Never show Excel line items in this table; totals still come from the API unchanged.
    return rows.filter(
      (row) => !/excel\s+transaction/i.test(String(row?.label ?? ''))
    );
  }, [balanceData]);

  const totals = useMemo(() => {
    if (!balanceData) return null;

    return {
      totalCredit: balanceData.secondaryTotalCredit || 0,
      totalDebit: balanceData.secondaryTotalDebit || 0,
      overallBalance: balanceData.secondaryOverallBalance || 0,
    };
  }, [balanceData]);

  const getRowClass = (row) => {
    if (row.type === 'debit') return 'debit-row';
    if (row.category === 'profit') return 'profit-row';
    return 'credit-row';
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="secondary-balance-container">
      <h2>Secondary Balance Enquiry</h2>

      <div className="filter-section">
        <h3>Date Duration</h3>
        <div className="date-filters">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className="apply-button" onClick={handleApplyFilter}>
              Apply Filter
            </button>
            <button className="clear-button" onClick={handleClearFilter}>
              Clear Filter
            </button>
          </div>
        </div>
        <p className="filter-note">
          {dateFrom && dateTo
            ? `Showing data between ${dateFrom} and ${dateTo}`
            : 'Showing cumulative data (no date filter applied).'}
        </p>
      </div>

      {loading ? (
        <div className="loading-message">Loading balance data…</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : !balanceData ? (
        <div className="no-data-message">No balance data available.</div>
      ) : (
        <div className="balance-table-section">
          <div className="print-header">
            <h3>Reference Numbers (System Credits / Debits)</h3>
            <button className="print-button" onClick={handlePrint}>
              Print
            </button>
          </div>
          <div className="print-title">
            <h1>Secondary Balance Enquiry</h1>
            <p className="print-date-range">
              {dateFrom && dateTo
                ? `Period: ${new Date(dateFrom).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to ${new Date(dateTo).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                : 'Cumulative Data (All Time)'}
            </p>
            <p className="print-generated-date">
              Generated on: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })} at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <table className="balance-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {balanceRows.map((row, index) => (
                <tr key={index} className={getRowClass(row)}>
                  <td>
                    {row.category === 'profit' && (
                      <span className="profit-badge">Profit</span>
                    )}
                    {row.label}
                  </td>
                  <td className={`right ${row.type === 'debit' ? 'debit-value' : 'credit-value'}`}>
                    {formatCurrency(row.value)}
                  </td>
                </tr>
              ))}
              
              {totals && (
                <>
                  {dailyBalances && (
                    <>
                      <tr className="total-row opening-balance">
                        <td><strong>Opening Balance</strong></td>
                        <td className={`right ${dailyBalances.secondaryOpeningBalance >= 0 ? 'credit-value' : 'debit-value'}`}>
                          <strong>{formatCurrency(dailyBalances.secondaryOpeningBalance)}</strong>
                        </td>
                      </tr>
                      <tr className="total-row closing-balance">
                        <td><strong>Closing Balance</strong></td>
                        <td className={`right ${dailyBalances.secondaryClosingBalance >= 0 ? 'credit-value' : 'debit-value'}`}>
                          <strong>{formatCurrency(dailyBalances.secondaryClosingBalance)}</strong>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr className="total-row total-credit">
                    <td><strong>Total Credit</strong></td>
                    <td className="right"><strong className="credit-value">{formatCurrency(totals.totalCredit)}</strong></td>
                  </tr>
                  <tr className="total-row total-debit">
                    <td><strong>Total Debit</strong></td>
                    <td className="right"><strong className="debit-value">{formatCurrency(totals.totalDebit)}</strong></td>
                  </tr>
                  <tr className="total-row overall-balance">
                    <td><strong>Legacy Overall Balance (Total Credit - Total Debit)</strong></td>
                    <td className={`right ${totals.overallBalance >= 0 ? 'credit-value' : 'debit-value'}`}>
                      <strong>{formatCurrency(totals.overallBalance)}</strong>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SecondaryBalanceEnquiry;
